import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { capitalize, formatCurrency, formatDate } from '../../../utils';
import { GROUP_USER } from '../../../config/appGroupConfig';
import { CustomButton } from '../../../components';
import onRefundClicked from './onRefundClicked';
import { TRANSACTION_TYPE } from '../../../constants/transaction-type';
import { refundBalance } from '../../../core/balance/action';
import { APP_ROUTES } from '../../../constants/app-routes';

export default function Row({ row, isCollapsible }) {
  const dispatch = useDispatch();
  const { user } = useSelector((store) => store.users);
  const [open, setOpen] = React.useState(false);

  // Map role IDs to display names
  const getRoleDisplayName = (roleId) => {
    const roleIdMap = {
      1: 'Vendor',
      2: 'Venue',
      5: 'Admin',
    };
    return roleIdMap[roleId] || `Role ${roleId}`;
  };

  // Get the actual role value for comparison (preserves GROUP_USER constants)
  const getRoleValue = (candidate) => {
    if (!candidate) return null;
    if (typeof candidate === 'string') return candidate;
    if (typeof candidate === 'object') {
      if (candidate.name) return candidate.name;
      if (candidate.role) return getRoleValue(candidate.role);
      if (candidate.group) return getRoleValue(candidate.group);
      if (candidate.role_name) return candidate.role_name;
      if (candidate.id) return candidate.id;
    }
    return candidate;
  };

  // Get display name for UI (maps IDs to readable names)
  const getRoleDisplayNameFromValue = (candidate) => {
    if (!candidate) return 'N/A';
    if (typeof candidate === 'string') {
      // If it's already a readable name or GROUP_USER constant, extract readable part
      if (candidate.includes('Vendor')) return 'Vendor';
      if (candidate.includes('Venue')) return 'Venue';
      if (candidate.includes('Admin')) return 'Admin';
      return candidate;
    }
    if (typeof candidate === 'object') {
      if (candidate.name) return getRoleDisplayNameFromValue(candidate.name);
      if (candidate.id) {
        const id = typeof candidate.id === 'number' ? candidate.id : parseInt(candidate.id, 10);
        if (!Number.isNaN(id)) return getRoleDisplayName(id);
      }
      return 'N/A';
    }
    if (typeof candidate === 'number') {
      return getRoleDisplayName(candidate);
    }
    return String(candidate);
  };

  const roleRaw = row?.role ?? row?.group ?? row?.role_name ?? row?.group_name ?? row?.roleName;
  const roleName = getRoleValue(roleRaw); // For comparison with GROUP_USER constants
  const roleDisplayName = getRoleDisplayNameFromValue(roleRaw); // For display in UI

  const onRefundConfirmed = (transactionId, amount) => {
    dispatch(refundBalance(transactionId, amount, user?.venue?.id));
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        {isCollapsible && (
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
        <TableCell align="left">{`${row.name} ${row.last_name}`}</TableCell>
        <TableCell align="left">{row.email}</TableCell>
        <TableCell align="left">{row?.phone_number}</TableCell>
        <TableCell align="left">{roleDisplayName}</TableCell>
      </TableRow>
      {isCollapsible && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Transactions
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell component="th">
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Event</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total amount ($)</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.transactions?.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell component="th" scope="row">
                          {formatDate(transaction.created_at, 'DD/MM/YYYY h:mma')}
                        </TableCell>
                        <TableCell>
                          {transaction.type === TRANSACTION_TYPE.REFUND ? 'Refund of ' : ''}
                          <Link
                            to={APP_ROUTES.EVENT_DETAILS.replace(':eventId', transaction.event.id)}>
                            {transaction.event.name}
                          </Link>
                        </TableCell>
                        <TableCell>{capitalize(transaction.type ?? '')}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(transaction.amount / 100.0)}
                        </TableCell>
                        <TableCell align="center">
                          {(roleName === GROUP_USER.VENDOR || roleRaw?.id === 1) &&
                            transaction.type === TRANSACTION_TYPE.PURCHARSE && (
                              <CustomButton
                                variant="contained"
                                onClick={() => onRefundClicked(transaction, onRefundConfirmed)}
                                disabled={transaction.hasChildren}>
                                Refund
                              </CustomButton>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

Row.propTypes = {
  row: PropTypes.instanceOf(Object).isRequired,
  isCollapsible: PropTypes.bool,
};

Row.defaultProps = {
  isCollapsible: false,
};
